import type { StepProps } from "../types";
import { Field, Input, Select } from "@/components/ui";

const PAYROLL_GROUPS = [
  { value: "director_fortnight", label: "Director fortnight" },
  { value: "field_fortnight",    label: "Field staff fortnight" },
  { value: "casual_weekly",      label: "Casual weekly" },
] as const;

export default function Step5Payroll({ context, draft, updateField }: StepProps) {
  if (!context.is_director) {
    return (
      <div className="text-text-muted text-sm py-4">
        This step is for directors only. A director will complete it with you.
      </div>
    );
  }

  const emp = context.employee;

  return (
    <div>
      <p className="text-text-muted text-sm mb-4">
        Payroll and bank details. Required to set up pay runs in Xero. These fields are director-only — field crew cannot see or edit them.
      </p>

      <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
        <h3 className="col-span-full text-sm font-medium text-text-muted uppercase tracking-wider2 mt-2 first:mt-0">
          Payroll
        </h3>

        <Field label="Payroll group *">
          <Select
            value={draft.payroll_group ?? emp?.payroll_group ?? ""}
            onChange={e => updateField("payroll_group", e.target.value)}
          >
            <option value="">—</option>
            {PAYROLL_GROUPS.map(g => (
              <option key={g.value} value={g.value}>{g.label}</option>
            ))}
          </Select>
        </Field>

        <div className="md:col-span-2">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              className="accent-accent"
              checked={draft.lafha_eligible ?? emp?.lafha_eligible ?? false}
              onChange={e => updateField("lafha_eligible", e.target.checked)}
            />
            <span className="text-[13px] text-text">
              LAFHA eligible — Living Away From Home Allowance
            </span>
          </label>
        </div>

        <h3 className="col-span-full text-sm font-medium text-text-muted uppercase tracking-wider2 mt-4">
          Bank details
        </h3>

        <Field label="BSB *">
          <Input
            type="text"
            inputMode="numeric"
            pattern="\d{3}-?\d{3}"
            maxLength={7}
            placeholder="123-456"
            value={draft.bank_bsb ?? emp?.bank_bsb ?? ""}
            onChange={e => updateField("bank_bsb", e.target.value)}
          />
        </Field>

        <Field label="Account number *">
          <Input
            type="text"
            inputMode="numeric"
            value={draft.bank_acct_number ?? emp?.bank_acct_number ?? ""}
            onChange={e => updateField("bank_acct_number", e.target.value)}
          />
        </Field>

        <h3 className="col-span-full text-sm font-medium text-text-muted uppercase tracking-wider2 mt-4">
          Superannuation
        </h3>

        <Field label="Super fund name *">
          <Input
            type="text"
            placeholder="e.g. AustralianSuper"
            value={draft.super_fund_name ?? emp?.super_fund_name ?? ""}
            onChange={e => updateField("super_fund_name", e.target.value)}
          />
        </Field>

        <Field label="USI *">
          <Input
            type="text"
            placeholder="e.g. STA0100AU"
            value={draft.super_fund_usi ?? emp?.super_fund_usi ?? ""}
            onChange={e => updateField("super_fund_usi", e.target.value)}
          />
        </Field>

        <div className="md:col-span-2">
          <Field label="Super member number *">
            <Input
              type="text"
              value={draft.super_member_number ?? emp?.super_member_number ?? ""}
              onChange={e => updateField("super_member_number", e.target.value)}
            />
          </Field>
        </div>
      </div>
    </div>
  );
}
